import { Buffer } from 'buffer';
import { useState } from 'react';
import { ALL_INFO_RETIREMENT_STEPS, MAX_SMALL_MOBILE_WIDTH, SESSION_KEY } from '../const/AppConst';
import { IJwtToken, IOptionCotisation, IRequestDataCreateRetirementProposition, IUpdatedProposition, MgpJwtPayload, IOffersDetails, IOption, IOldCompetitor, IPropositionDetails } from '../context/model';
import { AppState } from '../context/reducers/AppReducer';
import { decodeToken } from '../services/BaseApiService';
import { IInfoStep } from '../context/model/index';
import moment from 'moment';
import { getWindowDimensions } from '../hooks/useWindowDimensions';
import { compressFile } from '../services/CustomerApiService';
import platform from 'platform';
import { InfoContactFormData } from '../components/info-perso-template/InfoContactTemplate';


export function getLocalStorageValue(key: string) {
  const value = localStorage.getItem(key);
  if (!value) return null;
  try {
    return JSON.parse(decode(value));
  } catch (error) {
    return null;
  }
}

export function setLocalStorage(key: string, value: any) {
  localStorage.setItem(key, encode(JSON.stringify(value)));
}

export function getSessionStorageValue(key: string) {
  const value = sessionStorage.getItem(key);
  
  if (!value) return null;
  try {
    return JSON.parse(decode(value));
  } catch (error) {
    return null;
  }
}

export function setSessionStorage(key: string, value: any) {
  sessionStorage.setItem(key, encode(JSON.stringify(value)));
}

export const decode = (str: string):string => Buffer.from(str, 'base64').toString('binary');

export const encode = (str: string):string => Buffer.from(str, 'binary').toString('base64');

export const getUrlParam = (paramName: string) => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get(paramName);
}



export const scroll2Top = () => {
  window.scrollTo({top: 0, behavior: 'smooth'});
}

export const isMobile = () => {
  const {width} = getWindowDimensions();
  return width <= MAX_SMALL_MOBILE_WIDTH;
}

export const isDesktop = () => {
  const {width} = getWindowDimensions();
  return width > MAX_SMALL_MOBILE_WIDTH;
}

export const accessJsonAttr = (o: any, s: string) => {
    s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
    s = s.replace(/^\./, '');           // strip a leading dot
    let a = s.split('.');
    for (let i = 0, n = a.length; i < n; ++i) {
        let k = a[i];
        if (k in o) {
            o = o[k];
        } else {
            return;
        }
    }
    return o;
}

export function cleanUpSpecialChars(str:string)
{
    return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

export function useSessionStorage<T>(key: string, initialValue: T) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === "undefined") {
      return initialValue;
    }
    try {
      // Get from local storage by key
      const item = window.sessionStorage.getItem(key);
      if(item) {
        if(initialValue instanceof Map) {
          return new Map(JSON.parse(item));
        } else {
          return JSON.parse(item)
        }
      } else {
        return initialValue;
      }
      // Parse stored json or if none return initialValue

    } catch (error) {
      // If error also return initialValue
      console.log(error);
      return initialValue;
    }
  });
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to local storage
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(key, JSON.stringify(valueToStore instanceof Map ? Array.from(valueToStore.entries()): valueToStore));
      }
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.log(error);
    }
  };
  return [storedValue, setValue] as const;
}


export function equalArrayString(array1: string[], array2: string[]) {
  return array1.length === array2.length && array1.every((value, index) => value === array2[index]);
}

export const extractJwtToken = (jwt?: string | null, includeExpiredToken?: boolean) => {
  let jwtToken = null;
  if(!jwt) {
    return null;
  }
  const decodedJwt = decodeToken(jwt, includeExpiredToken) as MgpJwtPayload;
  if(decodedJwt) {
      jwtToken = {
        propositionId: decodedJwt.sub!,
        accessToken: jwt,
        roles: decodedJwt.roles,
        refreshToken: "",  // Temp
        expiration: decodedJwt.exp!,
        validateDob: decodedJwt.validateDob
      } as IJwtToken;
    }
    
    return jwtToken;
}

export const getStoreJwt = () => {
  const appState = getSessionStorageValue(SESSION_KEY) as AppState;

  if(appState?.jwtToken ) {
      return appState.jwtToken.accessToken;
  }
  return null;
}


export const hasValidateJwt = (jwtData: IJwtToken): boolean => {
  const current_time = Date.now().valueOf() / 1000;
  if(jwtData?.expiration && jwtData.expiration > current_time + 1000) {
      return true;
  }
  
  return false;
}

export function activateStep(path: string, steps: IInfoStep[], isRetired ?: boolean ): IInfoStep[] {
  steps.forEach((element, index) => {
    if(element.path === path) {
      steps[index].state = "ACTIVE";
      if (index > 0 && !isRetired) steps[index - 1].state = "SAVED";
    }
  })
  
  return steps;
}

export const isAcceptedFileSize = (f: File | null) => {
  const maxFileSize = 4194304 // 4MB
  if (f != null && f.size < maxFileSize) {
    return true;
  } else {
    return false;
  }
}

export const isAcceptedFileSizeMultiple = (f: File | null) => {
  const maxFileSize = 1048576 // 1MB
  if (f != null && f.size < maxFileSize) {
    return true;
  } else {
    return false;
  }
}

export const isValidFileSize = (f: File | null) => {
  const maxFileSize = 8388608 // 8MB
  if (f != null && f.size < maxFileSize) {
    return true;
  } else {
    return false;
  }
}

export const isValidFileType = (f: File | null) => {
  const validFileTypes: string[] = ['pdf', 'jpg', 'jpeg', 'png']
  const fileExt = (f?.type ) ? f.type.split("/")[1] : null
  return (fileExt != null)? validFileTypes.includes(fileExt) : false
}

export function isValidDate(val: string | undefined): boolean {
  if(val?.length === 10 && moment(val, "YYYY-MM-DD", true).isValid()) {
    const year =  Number(val.substring(0,4));
    return year > 1900 && year < 2099;
  }

  return false;
}

export function calculNirKey(nir: string): string {
  let nirStr = "";
  let nirNumeric = nir;
   if (nir.includes('2A')) {
     nirNumeric = nir.replace('2A', '19');
   } else if (nir.includes('2B')) {
     nirNumeric = nir.replace('2B', '18');
   }

  if(nirNumeric && Number(nirNumeric) && nirNumeric.length === 13) {
    const nirVal = 97 - Number(nirNumeric)%97;
    
    nirStr =  nirVal < 10 ? ("0" + nirVal) : nirVal.toString();
  }

  return nirStr;
}

export function formatNumeroSecu(input: string) {
 const clean = input.replace(/\s+/g, '').toUpperCase();
 
 if (clean.length < 13) return clean;

 const sexe = clean.slice(0, 1);
 const annee = clean.slice(1, 3);
 const mois = clean.slice(3, 5);
 const dept = clean.slice(5, 7); 
 const commune = clean.slice(7, 10);
 const ordre = clean.slice(10, 13);
 return [sexe, annee, mois, dept, commune, ordre].join(' ');
}

export  function getAge(dateString : string, checkedDateString?: string){
 
  let checkedDay = checkedDateString ? new Date(checkedDateString):  new Date();
  let birthDate = new Date(dateString);
  let age = checkedDay.getFullYear() - birthDate.getFullYear();
  let m = checkedDay.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && checkedDay.getDate() < birthDate.getDate())) {
      age--;
  }
  return age;
}

export function isValidIBAN(val : string){
  let codeLengths  = {
    AD: 24, AE: 23, AT: 20, AZ: 28, BA: 20, BE: 16, BG: 22, BH: 22, BR: 29,
    CH: 21, CR: 21, CY: 28, CZ: 24, DE: 22, DK: 18, DO: 28, EE: 20, ES: 24,
    FI: 18, FO: 18, FR: 27, GB: 22, GI: 23, GL: 18, GR: 27, GT: 28, HR: 21,
    HU: 28, IE: 22, IL: 23, IS: 26, IT: 27, JO: 30, KW: 30, KZ: 20, LB: 28,
    LI: 21, LT: 20, LU: 20, LV: 21, MC: 27, MD: 24, ME: 22, MK: 19, MR: 27,
    MT: 31, MU: 30, NL: 18, NO: 15, PK: 24, PL: 28, PS: 29, PT: 25, QA: 29,
    RO: 24, RS: 22, SA: 24, SE: 24, SI: 19, SK: 24, SM: 27, TN: 24, TR: 26
  };
let iban = String(val).toUpperCase().replace(/[^A-Z0-9]/g, ''); 
let code = iban.match(/^([A-Z]{2})(\d{2})([A-Z\d]+)$/); 
if (!code || iban.length !== (codeLengths as any)[code[1]]) {
  return false;
}
let digits = (code[3] + code[1] + code[2]).replace(/[A-Z]/g, (letter: string) => {
  return (letter.charCodeAt(0) - 55).toString();
});
return mod97(digits) === 1;

}

export function mod97(digital: number | string) {
  digital = digital.toString();
  let checksum: number | string = digital.slice(0, 2);
  let fragment = "";
  for (let offset = 2; offset < digital.length; offset += 7) {
    fragment = checksum + digital.substring(offset, offset + 7);
    checksum = parseInt(fragment, 10) % 97;
  }
  return checksum;
}
export function getFilename(headers: any) {
  const contentDisposition = headers['content-disposition'];
  if (contentDisposition) {
    const splitedStr = contentDisposition.split('filename=');
    if (splitedStr?.[1]) {
      return splitedStr[1];
    }
  }
  return "document.pdf"
}

export async function getCompressFile(token: string, file : File) {
  try {
      const res = await compressFile(token, file);
      const content = new Blob([res.data], {type: res.headers['content-type']});
      const filename= getFilename(res.headers);
      const newFile = new File([content], filename, {type: res.headers['content-type']});
      return newFile;
  } catch (err) {
    return Promise.reject(err)
  }
  
}

export function millisToMinutesAndSeconds(millis: number) {
  let minutes = Math.floor(millis / 60000);
  let seconds = Number(((millis % 60000) / 1000).toFixed(0));
  return minutes + `${minutes > 1 ? ' minutes ' : ' minute '}` + (seconds < 10 ? '0' : '') + seconds + `${seconds > 1 ? ' secondes' : ' seconde'}`;
}

export function isSupportedDocViewerBrower() {
  if(platform.name?.toLowerCase() === 'safari' && platform.version && platform.version.length > 0) {
      let majorVersion = Number(platform.version.split(".")[0]);
      return majorVersion > 15;
  }

  return true;
}

export function convertDateFormat(date : string ) {
  if ((/^\d{2}\/\d{2}\/\d{4}$/).test(date)) {
    let parts = date.split('/');
    let year = parts[2];
    let month = parts[1];
    let day = parts[0];

    if (month.length === 1) {
      month = '0' + month;
  }
  if (day.length === 1) {
      day = '0' + day;
  }
    return year + '-' + month + '-' + day;
  } else {
    return date;
  }
}

export function convertToLocaleDateString (date : string) {
  if ((/^\d{2}\/\d{2}\/\d{4}$/).test(date)) {
    return date;
  } else {
    return new Date(date).toLocaleDateString();
  }
}

export function formatDateFR(dateStr: string) {
  if (!dateStr) return "";
 
  const data = dateStr.split("T")[0];
  const parts = data.split("-");
 
  if (parts.length !== 3) return dateStr;
 
  const [year, month, day] = parts;
  return `${day}/${month}/${year}`;
}

export function handleKeyDown (event: React.KeyboardEvent) {
  if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
  }
};

export function getPriceOfOffer (offers: IOptionCotisation[], offerCode: string) {
  const price = offers.find(el => el.offerCode === offerCode)?.totalPrice;
  return price ? formatFrAmount(price) : '0 €'
};

export function getPriceOfOption (options: Record<string, IOffersDetails | null>, offerCode: string) {
  const price = options[offerCode]?.debitedAmountFromBankAccount!
  return price ? formatFrAmount(price) : '0 €'
};

export function getBodyRequestCreateREProposition (updatedProposition: IUpdatedProposition, formData: InfoContactFormData) {
  return {
          effectiveDate: updatedProposition?.effectiveDate ?? "",
          contact: {
            birthDate: updatedProposition?.birthDate,
            email: formData.email,
            civilityCode: Number(updatedProposition?.civilityCode),
            birthName: updatedProposition?.birthName,
            birthPlace: updatedProposition?.birthPlace,
            familySituationCode: Number(updatedProposition?.familySituationCode),
            nir: "",
            nirKey: "",
            regimeCode: null,
            address: updatedProposition?.address,
            additionalAddress: updatedProposition?.additionalAddress,
            buildingName: updatedProposition?.buildingName,
            lieudit: updatedProposition?.lieudit,
            city: updatedProposition?.city,
            postalCode: updatedProposition?.postalCode,
            countryId: updatedProposition?.countryId,
            hiringDate: "",
            phoneNumber: formData.phoneNumber,
            cspIndex: null,
            companyGrade: "",
            companyIndex: "",
            lastName: updatedProposition?.lastName,
            firstName: updatedProposition?.firstName,
            retiredDate: updatedProposition?.retirementDate
          },
          offerCode: updatedProposition?.offerCode
        } as IRequestDataCreateRetirementProposition
}


export function getInfoRetirementSteps (infoSteps: IInfoStep[], stepCode: string, isIncluded: boolean, lastStepCode: string) {
  const stepIndex = ALL_INFO_RETIREMENT_STEPS.findIndex(el => el.code === stepCode);
  if (stepIndex === -1 ) return {hasChanged: false, infoSteps: infoSteps};

  const step = ALL_INFO_RETIREMENT_STEPS[stepIndex];
  const existingIndex = infoSteps.findIndex(el => el.code === stepCode);
  const lastStepIndex = infoSteps.findIndex(el => el.code === lastStepCode);

  const newInfoSteps= [...infoSteps];
  let hasChanged = false;
  if (isIncluded) {
    if (existingIndex === -1) {
      newInfoSteps.splice(lastStepIndex + 1, 0, step);
      hasChanged = true;
    } 
  } else {
    if (existingIndex !== -1) {
      newInfoSteps.splice(existingIndex, 1);
      hasChanged = true;
    }
  }

  return {hasChanged, infoSteps: newInfoSteps}

};

export function getStepsWithBeneficiaryInfo (infoSteps: IInfoStep[], isIncluded: boolean) {
  const attestationSecuBeneStep = ALL_INFO_RETIREMENT_STEPS.find(el => el.code === "INFO_ATTES_BENE");
  const beneficiaryStep = ALL_INFO_RETIREMENT_STEPS.find(el => el.code === "INFO_BENE");

  const attestationSecuStepIndex = infoSteps.findIndex(el => el.code === "INFO_ATTES_RO");

  const existingIndex = infoSteps.findIndex(el => el.code === "INFO_ATTES_BENE");
  let newInfoSteps= [...infoSteps];
  let hasChanged = false;
  if (isIncluded) {
    if (existingIndex === -1) {
      newInfoSteps.splice(attestationSecuStepIndex + 1, 0, attestationSecuBeneStep!, beneficiaryStep!);
      hasChanged = true;
    } 
  } else {
    if (existingIndex !== -1) {
      newInfoSteps.splice(existingIndex, 2);
      hasChanged = true;
    }
  }

  return {hasChanged, infoSteps: newInfoSteps}
}

export function getValueNoSpace (data: string) {
  return data?.replace(/ /g, '');
}

export function formattedNir (nir: string) {
  return nir?.replace(/(\d{1})(\d{2})(\d{2})(\d{2})(\d{3})(\d{3})/, '$1 $2 $3 $4 $5 $6').trim();
}

export function formattedAttachmentOrganization (attachmentOrganization: string) {
  return attachmentOrganization?.replace(/(\d{2})(\d{3})(\d{4})/, '$1 $2 $3').trim();
}

export const formatFrAmount = (amount: number) : string => {
  return new Intl.NumberFormat("fr-FR", {maximumFractionDigits: 2, minimumFractionDigits: 2, style: "currency", currency: "EUR", currencyDisplay: "symbol"}).format(amount);
}

const coverageMap: Record<string, string> = {
  MIOM_BASE: "BASE",
  MIOM_OPT1: "BASE_OPTION1",
  MIOM_OPT2: "BASE_OPTION2"
};
export function mapCoverageType(type: string): string {
  return coverageMap[type];
}

export function getDayDifferent(date1 : any, date2? : any) {
  const d1 = new Date(date1);
  const d2 = new Date();

  const millisecondsDiff = d1.getTime() - d2.getTime()
  const diffDays = Math.floor(millisecondsDiff  / (1000 * 60 * 60 * 24));

  return diffDays;
}

export const getListOldContracts = (data: IOldCompetitor[]): IOption[] => {
  return data.map((item) => ({
    code: item.code ?? '',  
    label: item.name ?? '', 
  }));
};
export function createScriptElement(jsContent: string) {
  const script = document.createElement('script');
  script.innerHTML = jsContent;

  return script;
}

export function createScriptUrl(jsUrl: string, asynchrone: boolean) {
  const script = document.createElement('script');
  script.src = jsUrl;
  script.async = asynchrone;

  return script;
}

export function createUserFromPropo(proposition: IPropositionDetails | null ){
  return  {
    memberNumber: proposition?.contact.memberNumber,
    firstName: proposition?.contact.firstName,
    lastName: proposition?.contact?.lastName,
    department: proposition?.contact?.departmentCode,
    isCollaborator:true,
    contactId: proposition?.contact?.contactId, 
    email: proposition?.contact?.email,
    chatToken: '12655434156456',
    company: proposition?.companyLabel
  }
}

export function isValidBirthdateOfBeneficiary (birthDate: string | Date | null, effectiveDate: string | Date | null) {
  if (birthDate && effectiveDate) {
    return new Date(birthDate) < new Date(effectiveDate);
  } else {
    return false;
  }
}

export const getAllIndexByString = (string: string, key: string, ) => {
    let indexList = []

    for(var i = 0; i <string.length; i++){
        if(string[i] == key){
            indexList.push(i)
        }
    }
    return indexList
}

export const cleanSpecialCharAddress = (text: string) => {
  return text.replace(/[";]/g, "");
}

export function parseLocalDate(dateStr: string) {
 const [year, month, day] = dateStr.split('-').map(Number);
 return new Date(year, month - 1, day);
}

export const isWithinLast3Months = (dateStr: string) => {
 const now = new Date();
 const threeMonthsAgo = new Date();
 threeMonthsAgo.setMonth(now.getMonth() - 3);
 const date = parseLocalDate(dateStr);
 return date >= threeMonthsAgo && date <= now;
}


export const normalizeDate = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
